import { useState, useCallback } from 'react';
import type { AdResult } from '@/mocks/adResults';
import { getEdgeFunctionHeaders } from '@/lib/edgeFunctions';
import { getSupabaseFunctionUrl } from '@/lib/supabase';

const REGEN_ITEM_URL = getSupabaseFunctionUrl('regenerate-copy-item');

export type CopyType = 'hook' | 'headline' | 'subheadline' | 'primaryText' | 'cta';

export interface BusinessContext {
  businessName?: string;
  businessType?: string;
  businessDescription?: string;
  servicesOffered?: string;
  offer?: string;
  platform?: string;
  tone?: string;
  audience?: string;
  location?: string;
}

interface CopyItemProps {
  content: string;
  index: number;
  copyType: CopyType;
  isSaved: boolean;
  isRegenerating: boolean;
  onCopy: (text: string) => void;
  onSave: () => void;
  onRegenerate: () => void;
}

function CopyItem({
  content,
  index,
  isSaved,
  isRegenerating,
  onCopy,
  onSave,
  onRegenerate,
}: CopyItemProps) {
  const [copied, setCopied] = useState(false);

  const handleCopy = () => {
    onCopy(content);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  return (
    <div className={`group flex items-start gap-3 bg-white rounded-xl border p-4 transition-all duration-200 ${
      isSaved
        ? 'border-orange-200 bg-orange-50/40'
        : 'border-stone-100 hover:border-orange-200 hover:shadow-sm'
    }`}>
      <span className="shrink-0 mt-0.5 w-5 h-5 flex items-center justify-center rounded-full bg-stone-100 text-stone-400 text-xs font-bold group-hover:bg-orange-500 group-hover:text-white transition-colors">
        {index + 1}
      </span>

      {isRegenerating ? (
        <div className="flex-1 flex items-center gap-2 py-1">
          <i className="ri-loader-4-line animate-spin text-orange-400 text-sm shrink-0" />
          <span className="text-xs text-stone-400 italic">Writing a fresh version...</span>
        </div>
      ) : (
        <p className="flex-1 text-sm text-stone-700 leading-relaxed">{content}</p>
      )}

      {/* Action buttons */}
      <div className="shrink-0 flex items-center gap-1">
        {/* Regenerate — free */}
        <button
          onClick={onRegenerate}
          disabled={isRegenerating}
          title="Regenerate this item — free"
          className={`w-7 h-7 flex items-center justify-center rounded-lg border transition-all duration-200 cursor-pointer ${
            isRegenerating
              ? 'bg-stone-50 border-stone-200 text-stone-300 cursor-not-allowed'
              : 'bg-stone-50 border-stone-200 text-stone-400 hover:border-orange-400 hover:text-orange-500 hover:bg-orange-50'
          }`}
        >
          <i className={`${isRegenerating ? 'ri-loader-4-line animate-spin' : 'ri-refresh-line'} text-xs`} />
        </button>

        {/* Save / bookmark */}
        <button
          onClick={onSave}
          title={isSaved ? 'Remove from saved' : 'Save this copy'}
          className={`w-7 h-7 flex items-center justify-center rounded-lg border transition-all duration-200 cursor-pointer ${
            isSaved
              ? 'bg-orange-500 border-orange-500 text-white'
              : 'bg-stone-50 border-stone-200 text-stone-400 hover:border-orange-400 hover:text-orange-500 hover:bg-orange-50'
          }`}
        >
          <i className={`${isSaved ? 'ri-bookmark-fill' : 'ri-bookmark-line'} text-xs`} />
        </button>

        {/* Copy to clipboard */}
        <button
          onClick={handleCopy}
          title="Copy to clipboard"
          className={`w-7 h-7 flex items-center justify-center rounded-lg border transition-all duration-200 cursor-pointer ${
            copied
              ? 'bg-green-500 border-green-500 text-white'
              : 'bg-stone-50 border-stone-200 text-stone-400 hover:border-orange-400 hover:text-orange-500 hover:bg-orange-50'
          }`}
        >
          <i className={`${copied ? 'ri-check-line' : 'ri-file-copy-line'} text-xs`} />
        </button>
      </div>
    </div>
  );
}

/* ─── Section ────────────────────────────────────────────────────────────── */
interface SectionProps {
  icon: string;
  title: string;
  badge?: string;
  items: string[];
  copyType: CopyType;
  accentBg: string;
  accentText: string;
  savedKeys: Set<string>;
  regenLoadingKeys: Set<string>;
  onSaveToggle: (key: string) => void;
  onRegenItem: (copyType: CopyType, index: number, currentText: string) => void;
}

function CopySection({
  icon,
  title,
  badge,
  items,
  copyType,
  accentBg,
  accentText,
  savedKeys,
  regenLoadingKeys,
  onSaveToggle,
  onRegenItem,
}: SectionProps) {
  const handleCopy = (text: string) => {
    navigator.clipboard.writeText(text);
  };

  return (
    <div>
      <div className="flex items-center gap-2 mb-2.5">
        <div className={`w-6 h-6 flex items-center justify-center rounded-md ${accentBg}`}>
          <i className={`${icon} ${accentText} text-xs`} />
        </div>
        <h3 className="text-xs font-bold text-stone-800 uppercase tracking-widest">{title}</h3>
        {badge && (
          <span className={`text-xs font-semibold px-2 py-0.5 rounded-full ${accentBg} ${accentText}`}>{badge}</span>
        )}
        <span className="text-xs text-stone-400 ml-auto">{items.length} variations</span>
      </div>
      <div className="flex flex-col gap-2">
        {items.map((item, idx) => {
          const key = `${copyType}-${idx}`;
          return (
            <CopyItem
              key={key}
              content={item}
              index={idx}
              copyType={copyType}
              isSaved={savedKeys.has(key)}
              isRegenerating={regenLoadingKeys.has(key)}
              onCopy={handleCopy}
              onSave={() => onSaveToggle(key)}
              onRegenerate={() => onRegenItem(copyType, idx, item)}
            />
          );
        })}
      </div>
    </div>
  );
}

/* ─── Saved Copies Drawer ────────────────────────────────────────────────── */
interface SavedDrawerProps {
  savedItems: { key: string; text: string; label: string }[];
  onRemove: (key: string) => void;
  onCopyAll: () => void;
}

function SavedCopiesDrawer({ savedItems, onRemove, onCopyAll }: SavedDrawerProps) {
  const [open, setOpen] = useState(false);
  const [copiedAll, setCopiedAll] = useState(false);

  if (savedItems.length === 0) return null;

  const handleCopyAll = () => {
    onCopyAll();
    setCopiedAll(true);
    setTimeout(() => setCopiedAll(false), 2000);
  };

  return (
    <div className="rounded-xl border border-orange-200 bg-orange-50/60 overflow-hidden">
      <button
        onClick={() => setOpen((o) => !o)}
        className="w-full flex items-center gap-3 px-4 py-3 hover:bg-orange-50 transition-colors cursor-pointer"
      >
        <div className="w-6 h-6 flex items-center justify-center rounded-md bg-orange-500 text-white shrink-0">
          <i className="ri-bookmark-fill text-xs" />
        </div>
        <span className="text-xs font-bold text-stone-800 uppercase tracking-widest">Saved Copies</span>
        <span className="text-xs font-semibold px-2 py-0.5 rounded-full bg-orange-500 text-white ml-1">
          {savedItems.length}
        </span>
        <div className="ml-auto flex items-center gap-2">
          <button
            onClick={(e) => { e.stopPropagation(); handleCopyAll(); }}
            title="Copy all saved items"
            className={`flex items-center gap-1 text-xs font-semibold px-2.5 py-1 rounded-lg border transition-all duration-200 cursor-pointer whitespace-nowrap ${
              copiedAll
                ? 'bg-green-500 border-green-500 text-white'
                : 'bg-white border-orange-200 text-orange-600 hover:bg-orange-100'
            }`}
          >
            <i className={`${copiedAll ? 'ri-check-line' : 'ri-file-copy-line'} text-xs`} />
            {copiedAll ? 'Copied!' : 'Copy All'}
          </button>
          <i className={`${open ? 'ri-arrow-up-s-line' : 'ri-arrow-down-s-line'} text-stone-400 text-lg`} />
        </div>
      </button>

      {open && (
        <div className="px-4 pb-4 space-y-2 border-t border-orange-100">
          <p className="text-xs text-stone-400 pt-3 pb-1">Your bookmarked copy — ready to use in your campaigns.</p>
          {savedItems.map(({ key, text, label }) => (
            <div key={key} className="flex items-start gap-3 bg-white rounded-xl border border-orange-100 p-3">
              <div className="flex-1 min-w-0">
                <span className="text-xs font-semibold text-orange-500 uppercase tracking-wider">{label}</span>
                <p className="text-sm text-stone-700 leading-relaxed mt-0.5">{text}</p>
              </div>
              <div className="shrink-0 flex items-center gap-1">
                <button
                  onClick={() => navigator.clipboard.writeText(text)}
                  title="Copy"
                  className="w-7 h-7 flex items-center justify-center rounded-lg border border-stone-200 bg-stone-50 text-stone-400 hover:border-orange-400 hover:text-orange-500 transition-all duration-200 cursor-pointer"
                >
                  <i className="ri-file-copy-line text-xs" />
                </button>
                <button
                  onClick={() => onRemove(key)}
                  title="Remove from saved"
                  className="w-7 h-7 flex items-center justify-center rounded-lg border border-stone-200 bg-stone-50 text-stone-400 hover:border-red-300 hover:text-red-500 transition-all duration-200 cursor-pointer"
                >
                  <i className="ri-close-line text-xs" />
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

/* ─── Main CopyResultsSection ────────────────────────────────────────────── */
interface CopyResultsSectionProps {
  result: AdResult;
  businessContext: BusinessContext;
  isCopyRegenerating: boolean;
  copyRegenError: string | null;
  onRegenerateCopy: () => void;
  onClearCopyRegenError: () => void;
  /** Callback so parent can update result copy arrays when an item is re-rolled */
  onItemRegenerated: (copyType: CopyType, index: number, newText: string) => void;
}

export default function CopyResultsSection({
  result,
  businessContext,
  isCopyRegenerating,
  copyRegenError,
  onRegenerateCopy,
  onClearCopyRegenError,
  onItemRegenerated,
}: CopyResultsSectionProps) {
  // Saved items: key = `${copyType}-${index}`
  const [savedKeys, setSavedKeys] = useState<Set<string>>(new Set());
  // Per-item regen loading
  const [regenLoadingKeys, setRegenLoadingKeys] = useState<Set<string>>(new Set());
  const [regenErrors, setRegenErrors] = useState<Record<string, string>>({});

  const handleSaveToggle = useCallback((key: string) => {
    setSavedKeys((prev) => {
      const next = new Set(prev);
      if (next.has(key)) {
        next.delete(key);
      } else {
        next.add(key);
      }
      return next;
    });
  }, []);

  const handleRegenItem = useCallback(async (copyType: CopyType, index: number, currentText: string) => {
    const key = `${copyType}-${index}`;
    setRegenLoadingKeys((prev) => new Set(prev).add(key));
    setRegenErrors((prev) => { const n = { ...prev }; delete n[key]; return n; });

    try {
      const res = await fetch(REGEN_ITEM_URL, {
        method: 'POST',
        headers: await getEdgeFunctionHeaders(),
        body: JSON.stringify({
          copyType,
          currentText,
          ...businessContext,
        }),
      });

      const data = await res.json() as { text?: string; error?: string };
      if (!res.ok || data.error) {
        throw new Error(data.error ?? `Request failed (${res.status})`);
      }
      if (!data.text) throw new Error('No text returned');

      onItemRegenerated(copyType, index, data.text);

      // If this item was saved, remove the saved state since content changed
      setSavedKeys((prev) => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
    } catch (err) {
      setRegenErrors((prev) => ({
        ...prev,
        [key]: err instanceof Error ? err.message : 'Regeneration failed',
      }));
    } finally {
      setRegenLoadingKeys((prev) => {
        const next = new Set(prev);
        next.delete(key);
        return next;
      });
    }
  }, [businessContext, onItemRegenerated]);

  // Build saved items list for the drawer
  const SECTION_LABELS: Record<string, string> = {
    hook: 'Hook',
    headline: 'Headline',
    subheadline: 'Sub Headline',
    primaryText: 'Primary Text',
    cta: 'CTA',
  };

  const allItems: Record<CopyType, string[]> = {
    hook: result.hooks,
    headline: result.headlines,
    subheadline: result.subheadlines ?? [],
    primaryText: result.primaryText,
    cta: result.ctas,
  };

  const savedItems = Array.from(savedKeys)
    .map((key) => {
      const [type, idxStr] = key.split('-');
      const idx = parseInt(idxStr, 10);
      const arr = allItems[type as CopyType] ?? [];
      const text = arr[idx];
      if (!text) return null;
      return { key, text, label: SECTION_LABELS[type] ?? type };
    })
    .filter(Boolean) as { key: string; text: string; label: string }[];

  const handleCopyAll = () => {
    const text = savedItems.map((s) => `[${s.label}]\n${s.text}`).join('\n\n');
    navigator.clipboard.writeText(text);
  };

  const sections: {
    icon: string;
    title: string;
    badge: string;
    copyType: CopyType;
    items: string[];
    accentBg: string;
    accentText: string;
  }[] = [
    {
      icon: 'ri-fire-line',
      title: 'Hooks',
      badge: 'Attention grabbers',
      copyType: 'hook',
      items: result.hooks,
      accentBg: 'bg-orange-100',
      accentText: 'text-orange-500',
    },
    {
      icon: 'ri-heading',
      title: 'Headlines',
      badge: 'Conversion-focused',
      copyType: 'headline',
      items: result.headlines,
      accentBg: 'bg-amber-100',
      accentText: 'text-amber-600',
    },
    ...(result.subheadlines && result.subheadlines.length > 0
      ? [{
          icon: 'ri-text-snippet',
          title: 'Sub Headlines',
          badge: 'Supporting copy',
          copyType: 'subheadline' as CopyType,
          items: result.subheadlines,
          accentBg: 'bg-teal-100',
          accentText: 'text-teal-600',
        }]
      : []),
    {
      icon: 'ri-paragraph',
      title: 'Primary Text',
      badge: 'Body copy',
      copyType: 'primaryText',
      items: result.primaryText,
      accentBg: 'bg-rose-100',
      accentText: 'text-rose-500',
    },
    {
      icon: 'ri-cursor-fill',
      title: 'CTAs',
      badge: 'Call to action',
      copyType: 'cta',
      items: result.ctas,
      accentBg: 'bg-orange-100',
      accentText: 'text-orange-500',
    },
  ];

  return (
    <div className="space-y-6">
      {/* Section header */}
      <div className="flex items-center gap-3">
        <h3 className="text-xs font-bold text-stone-800 uppercase tracking-widest">Copy Results</h3>
        <div className="flex-1 h-px bg-stone-100" />
        <button
          onClick={onRegenerateCopy}
          disabled={isCopyRegenerating}
          title="Regenerate all copy — hooks, headlines, CTAs — without touching your images (5 credits)"
          className={`flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-lg border transition-all duration-200 whitespace-nowrap ${
            isCopyRegenerating
              ? 'bg-stone-100 text-stone-400 border-stone-200 cursor-not-allowed'
              : 'bg-white text-stone-600 border-stone-200 hover:border-orange-300 hover:text-orange-600 hover:bg-orange-50 cursor-pointer'
          }`}
        >
          {isCopyRegenerating ? (
            <><i className="ri-loader-4-line animate-spin text-xs" /> Rewriting all copy...</>
          ) : (
            <><i className="ri-text-spacing text-xs" /> Regenerate All <span className="text-stone-400 font-normal ml-1">· 5 cr</span></>
          )}
        </button>
      </div>

      {/* Regen all error */}
      {copyRegenError && (
        <div className="flex items-start gap-2.5 bg-red-50 border border-red-200 text-red-700 text-xs px-3 py-2.5 rounded-lg">
          <i className="ri-error-warning-line text-sm shrink-0 mt-0.5" />
          <span className="leading-snug flex-1">{copyRegenError}</span>
          <button onClick={onClearCopyRegenError} className="ml-auto shrink-0 text-red-400 hover:text-red-600 cursor-pointer">
            <i className="ri-close-line" />
          </button>
        </div>
      )}

      {/* Free regen hint */}
      <div className="flex items-center gap-2 bg-stone-50 border border-stone-100 rounded-lg px-3 py-2">
        <i className="ri-refresh-line text-orange-400 text-sm shrink-0" />
        <p className="text-xs text-stone-500 leading-snug">
          <strong className="text-stone-700">Tip:</strong> Click <i className="ri-refresh-line text-xs mx-0.5" /> on any individual copy item to re-roll just that one — completely free, no credits needed.
        </p>
      </div>

      {/* Saved copies drawer */}
      <SavedCopiesDrawer
        savedItems={savedItems}
        onRemove={(key) => setSavedKeys((prev) => { const n = new Set(prev); n.delete(key); return n; })}
        onCopyAll={handleCopyAll}
      />

      {/* Loading skeleton for full regen */}
      {isCopyRegenerating ? (
        <div className="space-y-6 animate-pulse">
          {['Hooks', 'Headlines', 'CTAs'].map((label) => (
            <div key={label}>
              <div className="flex items-center gap-2 mb-2.5">
                <div className="w-6 h-6 rounded-md bg-stone-100" />
                <div className="h-3 bg-stone-100 rounded w-20" />
                <div className="h-3 bg-stone-100 rounded w-28" />
              </div>
              <div className="flex flex-col gap-2">
                {[1, 2, 3].map((i) => (
                  <div key={i} className="flex items-start gap-3 bg-white rounded-xl border border-stone-100 p-4">
                    <div className="w-5 h-5 rounded-full bg-stone-100 shrink-0 mt-0.5" />
                    <div className="flex-1 space-y-1.5">
                      <div className="h-3 bg-stone-100 rounded w-full" />
                      <div className="h-3 bg-stone-100 rounded w-3/4" />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="space-y-6">
          {sections.map((s) => (
            <div key={s.copyType}>
              <CopySection
                icon={s.icon}
                title={s.title}
                badge={s.badge}
                items={s.items}
                copyType={s.copyType}
                accentBg={s.accentBg}
                accentText={s.accentText}
                savedKeys={savedKeys}
                regenLoadingKeys={regenLoadingKeys}
                onSaveToggle={handleSaveToggle}
                onRegenItem={handleRegenItem}
              />
              {/* Per-item regen errors for this section */}
              {s.items.map((_, idx) => {
                const key = `${s.copyType}-${idx}`;
                return regenErrors[key] ? (
                  <div key={key} className="flex items-start gap-2 bg-red-50 border border-red-200 text-red-700 text-xs px-3 py-2 rounded-lg mt-1.5">
                    <i className="ri-error-warning-line text-sm shrink-0 mt-0.5" />
                    <span className="flex-1 leading-snug">Item {idx + 1}: {regenErrors[key]}</span>
                    <button
                      onClick={() => setRegenErrors((prev) => { const n = { ...prev }; delete n[key]; return n; })}
                      className="shrink-0 text-red-400 hover:text-red-600 cursor-pointer"
                    >
                      <i className="ri-close-line" />
                    </button>
                  </div>
                ) : null;
              })}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
